import { Validator, type OutputUnit } from "@cfworker/json-schema";
import vegaSchema from "vega/vega-schema.json" with { type: "json" };

// The official Vega JSON schema, shipped with (and so always matching) the
// pinned `vega` dependency. Validated with @cfworker/json-schema because it
// interprets the schema rather than compiling it to code: Ajv's `new
// Function` codegen would trip the app's CSP (no 'unsafe-eval').

let validator: Validator | null = null;

function getValidator(): Validator {
  // Built once per session (~5 ms); validating a spec then takes ~1–2 ms.
  validator ??= new Validator(vegaSchema as object, "7", false);
  return validator;
}

const UNKNOWN_PROPERTY =
  /^Property "(.+)" does not match additional properties schema\.$/;

function depth(pointer: string): number {
  return pointer === "#" ? 0 : pointer.split("/").length - 1;
}

function toPointer(instanceLocation: string): string {
  const pointer = instanceLocation.replace(/^#/, "");
  return pointer === "" ? "/" : pointer;
}

/**
 * Condenses schema output into what an author can act on. The Vega schema is
 * a web of `oneOf`s, so one mistake fans out into dozens of errors, most
 * about alternatives the author never meant. The errors at the deepest
 * instance location are the ones about what the author actually wrote; an
 * unknown property is named outright.
 */
export function summarizeSchemaErrors(errors: readonly OutputUnit[]): string[] {
  const unknown = new Set<string>();
  for (const error of errors) {
    const match = UNKNOWN_PROPERTY.exec(error.error);
    if (error.keyword === "additionalProperties" && match) {
      unknown.add(
        `${toPointer(error.instanceLocation)}: unknown property "${match[1]}"`,
      );
    }
  }
  if (unknown.size > 0) return [...unknown];

  const deepest = Math.max(...errors.map((e) => depth(e.instanceLocation)));
  const messages = new Set<string>();
  for (const error of errors) {
    if (depth(error.instanceLocation) !== deepest) continue;
    if (error.keyword === "false") continue;
    messages.add(`${toPointer(error.instanceLocation)}: ${error.error}`);
  }
  return [...messages].slice(0, 5);
}

/** Validates a spec against the Vega schema; returns author-facing errors. */
export function validateVegaSchema(spec: unknown): string[] {
  const result = getValidator().validate(spec);
  return result.valid ? [] : summarizeSchemaErrors(result.errors);
}
